/* Inviting people into a group.
 *
 * ONE BOX FOR ONE PERSON AND FOR TWO HUNDRED. A separate "bulk import" screen
 * would be a second thing to find and a second thing to maintain; a textarea
 * that accepts one number per line handles both, and typing a single number is
 * a list of one. The API takes a list either way.
 *
 * The result is reported PER NUMBER, not as a total. An admin who pasted 200
 * lines and got "187 invited" has no way to act on the other 13 — so the
 * panel names them, split by reason.
 *
 * Numbers can be separated by newlines, commas or semicolons, because a list
 * copied out of a spreadsheet arrives in whichever of those the sheet used.
 */
import { useState } from 'react'
import { Check, Link2, Loader2, Send, Trash2, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import {
  useGroupInvitations,
  useInviteToGroup,
  useRevokeInvitation,
} from '@/api/queries'
import { cn, timeAgo } from '@/lib/utils'
import type { Invitation, InviteResult } from '@/types/api'

/** Split a pasted block into individual numbers.
 *
 *  Kept out of the component so the parsing can be reasoned about on its own:
 *  everything that is not a separator is a candidate, and the server is the
 *  one that decides whether a candidate is really a phone number. */
function parse(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((line) => line.trim())
    .filter(Boolean)
}

/** Copy one invitation's link.
 *
 *  Its own component for the copied state: a single flag on the panel would
 *  flash "Copied" beside every row at once, which reads as though the whole
 *  list had been copied.
 */
function CopyLink({ link, mobile }: { link: string; mobile: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      // Blocked without a secure context or permission. Silent: the leader can
      // still withdraw and re-invite, and an error toast for a clipboard is
      // more alarming than the problem.
    }
  }

  return (
    <button
      onClick={copy}
      aria-label={`Copy the invitation link for ${mobile}`}
      title="Copy the link to share"
      className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-brand"
    >
      {copied ? (
        <Check className="h-4 w-4 text-success" aria-hidden />
      ) : (
        <Link2 className="h-4 w-4" aria-hidden />
      )}
    </button>
  )
}

export function InvitePanel({
  churchId,
  groupId,
}: {
  churchId: string
  groupId: string
}) {
  const [raw, setRaw] = useState('')
  const [result, setResult] = useState<InviteResult | null>(null)
  // The invitation awaiting confirmation, or null. Holds the whole row so the
  // dialog can name who it is about.
  const [revoking, setRevoking] = useState<Invitation | null>(null)

  const invitations = useGroupInvitations(churchId, groupId)
  const invite = useInviteToGroup(churchId, groupId)
  const revoke = useRevokeInvitation(churchId, groupId)

  const numbers = parse(raw)

  function send() {
    if (!numbers.length || invite.isPending) return
    invite.mutate(
      numbers.map((mobile) => ({ mobile })),
      {
        onSuccess: (r) => {
          setResult(r)
          // Only clear when everything landed. Leaving the text in place after
          // a partial failure lets the admin fix the bad lines rather than
          // retyping the whole list.
          if (!r.invalid.length) setRaw('')
        },
      },
    )
  }

  return (
    <div className="space-y-s4">
      <Card>
        <div className="border-b border-line-2 p-s4">
          <h2 className="text-md font-bold">Invite people</h2>
          <p className="mt-1 text-base text-ink-2">
            One mobile number per line. They join when they accept — someone
            without an account yet is invited as soon as they register.
          </p>
        </div>

        <div className="space-y-s3 p-s4">
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={5}
            placeholder={'+91 98765 43210\n+91 91001 75371'}
            aria-label="Mobile numbers, one per line"
            className="w-full resize-y rounded border border-line bg-surface p-s3 font-mono text-base outline-none focus:border-brand focus:shadow-focus"
          />

          <div className="flex items-center justify-between gap-s3">
            <span className="text-base text-ink-2">
              {numbers.length
                ? `${numbers.length} ${numbers.length === 1 ? 'number' : 'numbers'}`
                : 'Nothing to send yet'}
            </span>
            <Button
              variant="primary"
              onClick={send}
              disabled={!numbers.length}
              loading={invite.isPending}
            >
              <Send className="h-4 w-4" aria-hidden />
              Send {numbers.length > 1 ? `${numbers.length} invites` : 'invite'}
            </Button>
          </div>

          {invite.isError ? (
            <p className="text-base text-danger">
              That did not send. Check the numbers and try again.
            </p>
          ) : null}

          {result ? <Report result={result} /> : null}
        </div>
      </Card>

      <Card>
        <div className="border-b border-line-2 p-s4">
          <h2 className="text-md font-bold">Invitations</h2>
        </div>

        {invitations.isLoading ? (
          <div className="flex justify-center py-s6">
            <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
          </div>
        ) : null}

        {!invitations.isLoading && !invitations.data?.length ? (
          <p className="px-s4 py-s6 text-center text-base text-ink-2">
            Nobody has been invited to this group yet.
          </p>
        ) : null}

        {invitations.data?.length ? (
          <ul className="divide-y divide-line-2">
            {invitations.data.map((i) => (
              <li key={i.id} className="flex items-center gap-s3 px-s4 py-s3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-surface-2 text-ink-2">
                  <UserPlus className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-md font-semibold">
                    {i.name || i.mobile}
                  </span>
                  <span className="block truncate text-base text-ink-2">
                    {i.name ? `${i.mobile} · ` : ''}
                    {timeAgo(i.created_at)}
                  </span>
                </span>

                <span
                  className={cn(
                    'shrink-0 rounded-pill px-s2 py-0.5 text-sm font-semibold',
                    i.status === 'accepted'
                      ? 'bg-success-soft text-success'
                      : i.status === 'declined'
                        ? 'bg-surface-2 text-ink-2'
                        : 'bg-warning-soft text-warning-ink',
                  )}
                >
                  {i.status === 'accepted'
                    ? 'Joined'
                    : i.status === 'declined'
                      ? 'Declined'
                      : 'Pending'}
                </span>

                {/* THE LINK, which is the point of the whole thing: a leader
                    has WhatsApp open and wants to paste something rather than
                    tell somebody to go and register. Only on pending ones —
                    an accepted invitation's link no longer works, and a Copy
                    button beside it would send something broken. */}
                {i.status === 'pending' && i.link ? (
                  <CopyLink link={i.link} mobile={i.mobile} />
                ) : null}

                {/* Only a pending invitation can be withdrawn — the others
                    already happened. */}
                {i.status === 'pending' ? (
                  <button
                    onClick={() => setRevoking(i)}
                    aria-label={`Withdraw the invitation to ${i.mobile}`}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </Card>

      <Confirm
        open={Boolean(revoking)}
        onOpenChange={(next) => !next && setRevoking(null)}
        title="Withdraw this invitation?"
        body={
          revoking
            ? `${revoking.name || revoking.mobile} will no longer be able to join from this invitation. You can invite them again later.`
            : undefined
        }
        confirmLabel="Withdraw"
        loading={revoke.isPending}
        onConfirm={() =>
          revoking &&
          revoke.mutate(revoking.id, { onSuccess: () => setRevoking(null) })
        }
      />
    </div>
  )
}

/** What happened to every number, split by reason. */
function Report({ result }: { result: InviteResult }) {
  const groups: { label: string; items: string[]; tone: string }[] = [
    {
      label: result.invited.length === 1 ? 'Invited' : 'Invited',
      items: result.invited,
      tone: 'text-success',
    },
    {
      label: 'Already in this group',
      items: result.already_member,
      tone: 'text-ink-2',
    },
    {
      label: 'Already invited',
      items: result.already_invited,
      tone: 'text-ink-2',
    },
    {
      label: 'Not a mobile number',
      items: result.invalid,
      tone: 'text-danger',
    },
  ].filter((g) => g.items.length)

  if (!groups.length) return null

  return (
    <div className="space-y-s2 rounded border border-line-2 bg-surface-2 p-s3">
      {groups.map(({ label, items, tone }) => (
        <div key={label}>
          <p className={cn('flex items-center gap-1.5 text-sm font-bold', tone)}>
            {tone === 'text-success' ? (
              <Check className="h-3.5 w-3.5" aria-hidden />
            ) : null}
            {label} ({items.length})
          </p>
          <p className="mt-0.5 break-words font-mono text-sm text-ink-2">
            {items.join(', ')}
          </p>
        </div>
      ))}
    </div>
  )
}
